import { Building2, CheckCircle2, Globe, Mail, MapPin, Phone, XCircle } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { adminApi, ApiError } from "../../../api/client";
import LoadingState from "../../../components/common/LoadingState";
import { useAdminAuth } from "../../../context/AdminAuthContext";
import { useToast } from "../../../context/ToastContext";
import type { OrganizerApplication, OrganizerApplicationStatus } from "../../../types/api";
import { formatDateTime } from "../../../utils/format";

const TABS: Array<{ key: OrganizerApplicationStatus | "all"; label: string }> = [
  { key: "pending", label: "Pending" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
  { key: "all", label: "All" },
];

const STATUS_BADGE: Record<OrganizerApplicationStatus, string> = {
  pending: "ego-badge ego-badge-warning",
  approved: "ego-badge ego-badge-success",
  rejected: "ego-badge ego-badge-danger",
};

export default function AdminExhibitorApplicationsPage() {
  const { token, admin } = useAdminAuth();
  const { showToast } = useToast();

  const [filter, setFilter] = useState<OrganizerApplicationStatus | "all">("pending");
  const [applications, setApplications] = useState<OrganizerApplication[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [rejectTarget, setRejectTarget] = useState<OrganizerApplication | null>(null);
  const [rejectReason, setRejectReason] = useState("");

  const canReview = admin?.role === "super-admin" || admin?.effectivePermissions?.includes("organizers:review");

  const load = useCallback(async () => {
    if (!token) return;
    setIsLoading(true);
    try {
      const result = await adminApi.listOrganizerApplications(
        token,
        filter === "all" ? undefined : filter
      );
      setApplications(result.applications);
    } catch (requestError) {
      showToast(
        requestError instanceof Error ? requestError.message : "Could not load applications",
        "error"
      );
    } finally {
      setIsLoading(false);
    }
  }, [token, filter, showToast]);

  useEffect(() => {
    void load();
  }, [load]);

  const approve = async (application: OrganizerApplication) => {
    if (!token) return;
    setBusyId(application.id);
    try {
      await adminApi.approveOrganizerApplication(token, application.id);
      showToast(`${application.businessName} approved — they can now sign in.`, "success");
      await load();
    } catch (requestError) {
      const message =
        requestError instanceof ApiError ? requestError.message :
        requestError instanceof Error ? requestError.message : "Approval failed";
      showToast(message, "error");
    } finally {
      setBusyId(null);
    }
  };

  const reject = async () => {
    if (!token || !rejectTarget) return;
    setBusyId(rejectTarget.id);
    try {
      await adminApi.rejectOrganizerApplication(token, rejectTarget.id, rejectReason);
      showToast(`${rejectTarget.businessName} has been rejected.`, "success");
      setRejectTarget(null);
      setRejectReason("");
      await load();
    } catch (requestError) {
      const message =
        requestError instanceof ApiError ? requestError.message :
        requestError instanceof Error ? requestError.message : "Rejection failed";
      showToast(message, "error");
    } finally {
      setBusyId(null);
    }
  };

  const sorted = useMemo(
    () =>
      [...applications].sort((a, b) => {
        const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return bTime - aTime;
      }),
    [applications]
  );

  if (!canReview) {
    return (
      <div className="ego-card p-8 text-center text-sm text-muted-foreground">
        You don't have permission to review exhibitor applications.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-2">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setFilter(tab.key)}
            className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
              filter === tab.key
                ? "bg-aan-green text-black"
                : "border border-white/15 bg-white/5 text-muted-foreground hover:border-aan-green hover:text-foreground"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <LoadingState label="Loading applications..." />
      ) : sorted.length === 0 ? (
        <div className="ego-card p-12 text-center text-sm text-muted-foreground">
          No {filter === "all" ? "" : filter} applications to show.
        </div>
      ) : (
        <div className="space-y-4">
          {sorted.map((application) => (
            <ApplicationCard
              key={application.id}
              application={application}
              busy={busyId === application.id}
              onApprove={() => approve(application)}
              onReject={() => {
                setRejectTarget(application);
                setRejectReason("");
              }}
            />
          ))}
        </div>
      )}

      {rejectTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 dark:bg-black/75 p-4">
          <div className="w-full max-w-md rounded-2xl bg-surface p-6 shadow-2xl">
            <h3 className="text-lg font-bold text-foreground">
              Reject {rejectTarget.businessName}?
            </h3>
            <p className="mt-1 text-sm text-muted-foreground">
              The applicant will see this reason on the status page.
            </p>
            <textarea
              rows={4}
              placeholder="Reason (optional)"
              value={rejectReason}
              onChange={(event) => setRejectReason(event.target.value)}
              className="mt-4 w-full rounded-xl border border-line bg-surface px-3 py-2.5 text-sm outline-none focus:border-[var(--pp-gold)] focus:ring-4 focus:ring-[rgba(245,179,3,0.18)]"
            />
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setRejectTarget(null);
                  setRejectReason("");
                }}
                className="rounded-xl px-4 py-2 text-sm font-semibold text-muted-foreground hover:bg-surface-muted"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={reject}
                disabled={busyId === rejectTarget.id}
                className="rounded-xl bg-[#dc2626] px-4 py-2 text-sm font-bold text-white hover:bg-[#b91c1c] disabled:opacity-60"
              >
                {busyId === rejectTarget.id ? "Rejecting..." : "Reject application"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ApplicationCard({
  application,
  busy,
  onApprove,
  onReject,
}: {
  application: OrganizerApplication;
  busy: boolean;
  onApprove: () => void;
  onReject: () => void;
}) {
  const isPending = application.status === "pending";

  return (
    <div className="ego-card overflow-hidden">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-line bg-surface-muted px-5 py-4">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-base font-bold text-foreground">{application.businessName}</span>
            <span className={STATUS_BADGE[application.status]}>{application.status}</span>
          </div>
          <div className="mt-0.5 text-xs text-muted-foreground">
            Submitted{" "}
            {application.createdAt ? formatDateTime(application.createdAt) : "recently"}
          </div>
        </div>

        {isPending && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onReject}
              disabled={busy}
              className="flex items-center gap-1.5 rounded-xl border border-line bg-surface px-3 py-2 text-sm font-semibold text-muted-foreground transition hover:bg-surface-muted disabled:opacity-60"
            >
              <XCircle size={15} />
              Reject
            </button>
            <button
              type="button"
              onClick={onApprove}
              disabled={busy}
              className="flex items-center gap-1.5 rounded-xl bg-[#16a34a] px-3 py-2 text-sm font-bold text-white transition hover:bg-[#15803d] disabled:opacity-60"
            >
              <CheckCircle2 size={15} />
              {busy ? "Approving..." : "Approve"}
            </button>
          </div>
        )}
      </div>

      <div className="grid gap-4 px-5 py-4 sm:grid-cols-2">
        <DetailRow icon={Building2} label="Exhibitor name" value={application.name} />
        <DetailRow icon={Mail} label="Email" value={application.email} />
        <DetailRow icon={Phone} label="Phone" value={application.phone} />
        {application.city && <DetailRow icon={MapPin} label="City" value={application.city} />}
        {application.website && (
          <DetailRow icon={Globe} label="Website" value={application.website} />
        )}
      </div>

      {application.description && (
        <div className="border-t border-line px-5 py-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-[#9aa3b2]">
            About
          </div>
          <p className="mt-1 text-sm leading-6 text-foreground">{application.description}</p>
        </div>
      )}

      {application.status === "rejected" && application.rejectionReason && (
        <div className="border-t border-line bg-[#fef2f2] dark:bg-[rgba(239,68,68,0.14)] px-5 py-3 text-sm text-[#991b1b]">
          <span className="font-semibold">Rejection reason:</span> {application.rejectionReason}
        </div>
      )}

      {application.status === "approved" && application.reviewedAt && (
        <div className="border-t border-line bg-[#f0fdf4] dark:bg-[rgba(16,185,129,0.14)] px-5 py-3 text-sm text-[#166534]">
          Approved {formatDateTime(application.reviewedAt)}
          {application.reviewedByEmail && ` by ${application.reviewedByEmail}`}
        </div>
      )}
    </div>
  );
}

function DetailRow({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ComponentType<{ size?: number; className?: string }>;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-start gap-2.5">
      <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[#fff1ea] dark:bg-[rgba(245,158,11,0.14)] text-[#c2410c]">
        <Icon size={14} />
      </div>
      <div className="min-w-0">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-[#9aa3b2]">
          {label}
        </div>
        <div className="truncate text-sm font-semibold text-foreground">{value}</div>
      </div>
    </div>
  );
}
